//#region Imports
import * as React from "react";
import { useSubscription, Subscription } from "use-subscription";
import type { IAnydoAuth } from "@anydo/interfaces";
import { useAnalytics } from "@anydo/analytics-react";
import type { IAuthInternalContextValue } from "./auth-react-types";
import { AuthContext } from "./AuthContext";
import { AuthReactAdapter } from "./-private/AuthReactAdapter";
import type { ReadonlyAuthReactAdapterStateStateT } from "./-private/AuthReactAdapterState";
import { AuthServiceContext } from "./AuthServiceContext";
//#endregion

//#region Types
type PropsT = {
  children: React.ReactNode;
  // Services
  auth: IAnydoAuth;
};
//#endregion

// analytics: Analytics,
function AuthProvider({ auth, children }: PropsT) {
  const analytics = useAnalytics();

  //#region Auth React Adapter
  const { ara, actions } = React.useMemo(() => {
    const ara = new AuthReactAdapter(auth, analytics);

    const actions = {
      sendPremiumUserStrpieInvoices: ara.sendPremiumUserStrpieInvoices.bind(ara),
      resetStatusError: ara.resetStatusError.bind(ara),
      join: ara.join.bind(ara),
      loginWithApple: ara.loginWithApple.bind(ara),
      loginWithEmail: ara.loginWithEmail.bind(ara),
      preLoginWithEmail2fa: ara.preLoginWithEmail2fa.bind(ara),
      loginWithEmail2fa: ara.loginWithEmail2fa.bind(ara),
      loginWithFacebook: ara.loginWithFacebook.bind(ara),
      loginWithGoogle: ara.loginWithGoogle.bind(ara),
      logout: ara.logout.bind(ara),
      deleteUser: ara.deleteUser.bind(ara),
      completeOnboarding: ara.completeOnboarding.bind(ara),
    };

    return { ara, actions };
  }, [auth, analytics]);

  const state = useAraState(ara);

  useUpdateTimeZoneByLogin(auth, state.isLoggedIn);
  //#endregion
  const authState = React.useMemo<IAuthInternalContextValue>(() => {
    const userEmail = state.userDetails == null ? "" : state.userDetails.email;
    const userProfileImageUrl =
      state.userDetails == null ? null : state.userDetails.profilePicture || state.userDetails.googleAvatarUrl;

    const isPhoneVerified =
      state.isLoggedIn && state.userDetails != null && state.userDetails.verifiedPhoneNumber != null;

    return {
      auth,

      //#region State
      ...state,
      userEmail,
      userProfileImageUrl,
      //#endregion

      //#region Phone verification
      isPhoneVerified,
      //#endregion

      //#region Block actions
      ...actions,
      //#endregion
    };
  }, [state, auth, actions]);

  return (
    <AuthServiceContext.Provider value={auth}>
      <AuthContext.Provider value={authState}>{children}</AuthContext.Provider>
    </AuthServiceContext.Provider>
  );
}

const AuthProviderMemo = React.memo(AuthProvider);
export { AuthProviderMemo as AuthProvider };

//#region Private hooks
function useAraState(ara: AuthReactAdapter) {
  const subscription = React.useMemo((): Subscription<ReadonlyAuthReactAdapterStateStateT> => {
    return {
      getCurrentValue: () => ara.getState(),
      subscribe: (listener) => ara.subscribe(listener),
    };
  }, [ara]);

  const state = useSubscription(subscription);

  return state;
}

function useUpdateTimeZoneByLogin(auth: IAnydoAuth, isLoggedIn: boolean) {
  React.useEffect(() => {
    if (isLoggedIn) {
      auth.updateTimeZone();
    }
  }, [auth, isLoggedIn]);
}
//#endregion
